import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { CircleNotch, ArrowLeft, ArrowRight, FloppyDisk, CheckCircle, Plus, Trash } from "@phosphor-icons/react";
import CoPilotPanel, { CoPilotToggle } from "@/components/CoPilotPanel";
import { calDisposition, DISPOSITIONS } from "@/components/blueprints/CalibrationBlueprint";

const STEPS = [
  { n: 1, code: "1", label: "Team Input", phase: "PEOPLE" },
  { n: 2, code: "2", label: "Performance / Values", phase: "PEOPLE" },
  { n: 3, code: "3", label: "Calibration", phase: "PEOPLE" },
  { n: 4, code: "4", label: "Development Actions", phase: "PEOPLE" },
];
const LAST_STEP = STEPS.length;
const uid = (p) => p + "_" + Math.random().toString(36).slice(2, 10);

const labelCls = "block text-xs font-semibold tracking-[0.08em] uppercase text-slate-500 mb-1.5";
const inputCls = "flex h-10 w-full rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent";
const taCls = "w-full rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent";

const HiLo = ({ on, onClick, testid }) => (
  <button data-testid={testid} onClick={onClick}
    className={`px-3 py-1.5 rounded-sm text-xs font-semibold transition-all ${on ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
    {on ? "High" : "Low"}
  </button>
);

export default function CalibrationMatrix() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [context, setContext] = useState("");
  const [members, setMembers] = useState([]);
  const [coPilotOpen, setCoPilotOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/documents/${id}`);
        setTitle(res.data.title || "");
        const p = res.data.payload || {};
        setContext(p.context || "");
        setMembers((p.members || []).map((m) => ({ performance: false, values: false, disposition: "", action: "", owner: "", role: "", ...m })));
      } catch {
        toast.error("Document not found");
        navigate("/dashboard");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  const persist = useCallback(async (extra = {}) => {
    setSaving(true);
    try {
      await api.put(`/documents/${id}`, { title: title || "Untitled Calibration", payload: { context, members }, ...extra });
      return true;
    } catch {
      toast.error("Save failed");
      return false;
    } finally {
      setSaving(false);
    }
  }, [id, title, context, members]);

  const saveDraft = async () => { if (await persist()) toast.success("Draft saved"); };
  const next = async () => { await persist(); setStep((s) => Math.min(LAST_STEP, s + 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const back = () => { setStep((s) => Math.max(1, s - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const finalise = async () => {
    if (await persist({ status: "Completed" })) {
      toast.success("Calibration finalised");
      navigate(`/calibration/${id}/view`);
    }
  };

  const addMember = () => setMembers((m) => [...m, { id: uid("mem"), name: "", role: "", performance: false, values: false, disposition: "", action: "", owner: "" }]);
  const setMember = (idx, patch) => setMembers((m) => m.map((x, i) => (i === idx ? { ...x, ...patch } : x)));
  const removeMember = (idx) => setMembers((m) => m.filter((_, i) => i !== idx));
  const autoDisposition = () => setMembers((m) => m.map((x) => ({ ...x, disposition: x.disposition || calDisposition(x.performance, x.values) })));

  const applySuggestion = (text) => {
    if (step === 1) { setMembers((m) => [...m, { id: uid("mem"), name: text, role: "", performance: false, values: false, disposition: "", action: "", owner: "" }]); toast.success("Added"); }
    else { navigator.clipboard?.writeText(text); toast.info("Suggestion copied to clipboard"); }
  };

  if (loading)
    return <Layout><div className="flex items-center justify-center py-40 text-slate-400"><CircleNotch size={28} className="animate-spin" /></div></Layout>;

  const valid = members.filter((m) => m.name?.trim());

  return (
    <Layout>
      <div className="max-w-5xl mx-auto w-full py-8 px-6 lg:px-10">
        <button data-testid="back-link" onClick={() => navigate("/dashboard")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-3">
          <ArrowLeft size={16} /> Workspace
        </button>
        <input data-testid="calibration-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Calibration title…"
          className="w-full bg-transparent text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 placeholder:text-slate-300 focus:outline-none border-b border-transparent focus:border-slate-300 pb-1" />

        <div className="mt-5 flex items-center gap-2" data-testid="calibration-stepper">
          {STEPS.map((s) => (
            <button key={s.n} data-testid={`step-${s.n}`} onClick={() => setStep(s.n)}
              className={`flex-1 rounded-sm border px-3 py-2.5 text-left transition-all ${step === s.n ? "border-emerald-500 bg-emerald-50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
              <span className={`font-mono text-xs font-bold ${step === s.n ? "text-emerald-700" : "text-slate-400"}`}>STEP {s.n}</span>
              <p className={`text-sm font-medium ${step === s.n ? "text-slate-900" : "text-slate-500"}`}>{s.label}</p>
            </button>
          ))}
        </div>

        <div className="mt-6 bg-white border border-slate-200 rounded-sm shadow-sm p-6">
          {step === 1 && (
            <div className="space-y-5">
              <h2 className="text-xl font-semibold text-slate-900">Who are you calibrating?</h2>
              <div>
                <label className={labelCls}>Calibration Context</label>
                <textarea data-testid="field-context" rows={3} className={taCls} value={context} onChange={(e) => setContext(e.target.value)} placeholder="What cycle, team or cohort is this calibration for? (optional)" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className={labelCls}>Team Members</label>
                  <button data-testid="add-member-button" onClick={addMember} className="inline-flex items-center gap-1.5 rounded-sm border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50">
                    <Plus size={16} /> Add member
                  </button>
                </div>
                {members.length === 0 && <p className="text-sm text-slate-400 border border-dashed border-slate-300 rounded-sm p-4 text-center">No members yet.</p>}
                <div className="space-y-2">
                  {members.map((m, i) => (
                    <div key={m.id} className="flex items-center gap-2">
                      <span className="font-mono text-xs text-slate-400 w-5">{i + 1}.</span>
                      <input data-testid={`member-name-${i}`} value={m.name} onChange={(e) => setMember(i, { name: e.target.value })} placeholder="Name…" className={inputCls + " flex-1"} />
                      <input data-testid={`member-role-${i}`} value={m.role} onChange={(e) => setMember(i, { role: e.target.value })} placeholder="Role…" className={inputCls + " flex-1"} />
                      <button data-testid={`remove-member-${i}`} onClick={() => removeMember(i)} className="p-2 text-slate-400 hover:text-red-600"><Trash size={16} /></button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <h2 className="text-xl font-semibold text-slate-900">Rate performance & values</h2>
              <p className="text-sm text-slate-500">Judge <strong>Performance</strong> (results delivered) and <strong>Values</strong> (how they were delivered) independently.</p>
              {valid.length === 0 ? <p className="text-sm text-slate-400 border border-dashed border-slate-300 rounded-sm p-4 text-center">Add members in Step 1 first.</p> : (
                <div className="space-y-2">
                  {members.map((m, i) => {
                    if (!m.name?.trim()) return null;
                    return (
                      <div key={m.id} data-testid={`rate-row-${i}`} className="flex flex-col sm:flex-row sm:items-center gap-3 border border-slate-200 rounded-sm p-3">
                        <span className="flex-1 text-sm text-slate-800">{m.name}{m.role && <span className="text-slate-400"> · {m.role}</span>}</span>
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2"><span className="text-xs text-slate-500 uppercase tracking-wide">Perf.</span><HiLo testid={`performance-${i}`} on={m.performance} onClick={() => setMember(i, { performance: !m.performance })} /></div>
                          <div className="flex items-center gap-2"><span className="text-xs text-slate-500 uppercase tracking-wide">Values</span><HiLo testid={`values-${i}`} on={m.values} onClick={() => setMember(i, { values: !m.values })} /></div>
                          <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{calDisposition(m.performance, m.values)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5">
              <h2 className="text-xl font-semibold text-slate-900">Confirm each disposition</h2>
              <p className="text-sm text-slate-500">The grid suggests a default — override where your judgement differs.</p>
              {valid.length === 0 ? <p className="text-sm text-slate-400 border border-dashed border-slate-300 rounded-sm p-4 text-center">Add and rate members first.</p> : (
                <div className="space-y-2">
                  {members.map((m, i) => {
                    if (!m.name?.trim()) return null;
                    const current = m.disposition || calDisposition(m.performance, m.values);
                    return (
                      <div key={m.id} data-testid={`disp-row-${i}`} className="flex flex-col lg:flex-row lg:items-center gap-3 border border-slate-200 rounded-sm p-3">
                        <span className="flex-1 text-sm text-slate-800">{m.name}</span>
                        <div className="inline-flex flex-wrap rounded-sm bg-slate-100 p-1">
                          {DISPOSITIONS.map((d) => (
                            <button key={d} data-testid={`disposition-${i}-${d.split(" ")[0].toLowerCase()}`} onClick={() => setMember(i, { disposition: d })}
                              className={`px-3 py-1.5 rounded-sm text-xs font-medium transition-all ${current === d ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>{d}</button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {step === 4 && (
            <div className="space-y-5">
              <h2 className="text-xl font-semibold text-slate-900">Development actions</h2>
              <p className="text-sm text-slate-500">Give every member a concrete, owned next step.</p>
              {valid.length === 0 ? <p className="text-sm text-slate-400 border border-dashed border-slate-300 rounded-sm p-4 text-center">Add members first.</p> : (
                <div className="space-y-3">
                  {members.map((m, i) => {
                    if (!m.name?.trim()) return null;
                    return (
                      <div key={m.id} data-testid={`action-row-${i}`} className="border border-slate-200 rounded-sm p-4">
                        <p className="text-sm font-medium text-slate-900 mb-3">{m.name} <span className="font-normal text-slate-400">· {m.disposition || calDisposition(m.performance, m.values)}</span></p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="md:col-span-2">
                            <label className={labelCls}>Next Action</label>
                            <input data-testid={`member-action-${i}`} value={m.action} onChange={(e) => setMember(i, { action: e.target.value })} placeholder="Concrete development step" className={inputCls} />
                          </div>
                          <div>
                            <label className={labelCls}>Owner</label>
                            <input data-testid={`member-owner-${i}`} value={m.owner} onChange={(e) => setMember(i, { owner: e.target.value })} placeholder="Who drives it?" className={inputCls} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mt-6 flex items-center justify-between gap-3 sticky bottom-0 bg-slate-50 py-4">
          <button data-testid="calibration-back" onClick={back} disabled={step === 1}
            className="inline-flex items-center gap-2 rounded-sm border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed">
            <ArrowLeft size={16} /> Back
          </button>
          <div className="flex items-center gap-3">
            <button data-testid="calibration-save-draft" onClick={saveDraft} disabled={saving}
              className="inline-flex items-center gap-2 rounded-sm border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50">
              {saving ? <CircleNotch size={16} className="animate-spin" /> : <FloppyDisk size={16} />} Save Draft
            </button>
            {step < LAST_STEP ? (
              <button data-testid="calibration-next" onClick={() => { if (step === 2) autoDisposition(); next(); }}
                className="inline-flex items-center gap-2 rounded-sm bg-emerald-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-emerald-700">
                Next Step <ArrowRight size={16} />
              </button>
            ) : (
              <button data-testid="calibration-finalise" onClick={finalise} disabled={saving}
                className="inline-flex items-center gap-2 rounded-sm bg-emerald-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-emerald-700">
                <CheckCircle size={18} weight="fill" /> Finalise Calibration
              </button>
            )}
          </div>
        </div>
      </div>

      <CoPilotToggle open={coPilotOpen} onClick={() => setCoPilotOpen(true)} phase={STEPS[step - 1].phase} />
      <CoPilotPanel open={coPilotOpen} onClose={() => setCoPilotOpen(false)} docId={id} toolType="calibration" step={STEPS[step - 1]} phase={STEPS[step - 1].phase} onApplySuggestion={applySuggestion} />
    </Layout>
  );
}
